import React, { useState, useMemo, useCallback } from 'react';

// ════════════════════════════════════════════════════════════════
//  TYPES
// ════════════════════════════════════════════════════════════════

type Category = 'All' | 'MERN' | 'React' | 'Node.js' | 'Mysql' | 'TypeScript' | 'AWS' | 'Microservices' | 'Real-Time';

interface ProjectMedia {
  type: 'image' | 'youtube';
  /** For image: direct image URL. For youtube: video ID or playlist URL */
  url: string;
  caption: string;
  /** Optional thumbnail override for youtube entries shown in grid/strip */
  thumb?: string;
}

interface Project {
  id: number;
  title: string;
  tagline: string;
  description: string;
  category: Category[];
  tech: string[];
  liveUrl?: string;
  codeUrl?: string;
  featured: boolean;
  status: 'Live' | 'In Progress' | 'Archived';
  metrics?: { label: string; value: string }[];
  accentColor: string;
  icon: string;
  media: ProjectMedia[];
}

interface DesignStep {
  phase: string;
  component: string;
  technology: string;
  purpose: string;
  scalability: string;
  notes: string;
}

interface SystemDesign {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  accentColor: string;
  scale: string;
  overview: string[];
  steps: DesignStep[];
  keyDecisions: { decision: string; rationale: string }[];
  achievements: string[];
}

// ════════════════════════════════════════════════════════════════
//  HELPERS — YouTube
// ════════════════════════════════════════════════════════════════

/**
 * Given a YouTube URL or bare video ID, return the embeddable src string.
 * Supports:
 *  - https://www.youtube.com/watch?v=VIDEO_ID
 *  - https://youtu.be/VIDEO_ID
 *  - https://www.youtube.com/playlist?list=PLAYLIST_ID
 *  - https://www.youtube.com/embed/VIDEO_ID
 *  - Bare VIDEO_ID (11 chars)
 */
function getYouTubeEmbedUrl(raw: string): string {
  // Already an embed URL
  if (raw.includes('youtube.com/embed')) return raw;

  // Playlist
  const plMatch = raw.match(/[?&]list=([^&]+)/);
  if (plMatch) {
    return `https://www.youtube.com/embed/videoseries?list=${plMatch[1]}&rel=0&modestbranding=1`;
  }

  // Standard watch URL
  const vMatch = raw.match(/[?&]v=([^&]{11})/);
  if (vMatch) return `https://www.youtube.com/embed/${vMatch[1]}?rel=0&modestbranding=1`;

  // youtu.be short URL
  const shortMatch = raw.match(/youtu\.be\/([^?&]{11})/);
  if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}?rel=0&modestbranding=1`;

  // Bare 11-char video ID
  if (/^[a-zA-Z0-9_-]{11}$/.test(raw.trim())) {
    return `https://www.youtube.com/embed/${raw.trim()}?rel=0&modestbranding=1`;
  }

  return raw; // fallback
}

/**
 * Generate a YouTube thumbnail URL from the raw input.
 */
function getYouTubeThumbnail(raw: string): string {
  const plMatch = raw.match(/[?&]list=([^&]+)/);
  if (plMatch) return 'https://img.youtube.com/vi/0/mqdefault.jpg'; // playlist — no easy thumb

  const vMatch = raw.match(/[?&]v=([^&]{11})/);
  if (vMatch) return `https://img.youtube.com/vi/${vMatch[1]}/mqdefault.jpg`;

  const shortMatch = raw.match(/youtu\.be\/([^?&]{11})/);
  if (shortMatch) return `https://img.youtube.com/vi/${shortMatch[1]}/mqdefault.jpg`;

  if (/^[a-zA-Z0-9_-]{11}$/.test(raw.trim())) {
    return `https://img.youtube.com/vi/${raw.trim()}/mqdefault.jpg`;
  }

  return '';
}

/** Resolve the thumbnail for any media item */
function mediaThumbnail(m: ProjectMedia): string {
  if (m.type === 'image') return m.url;
  return m.thumb || getYouTubeThumbnail(m.url) || 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&q=60';
}

// ════════════════════════════════════════════════════════════════
//  PROJECTS DATA  (from CV — real projects)
// ════════════════════════════════════════════════════════════════

const PROJECTS: Project[] = [
  {
    id: 1,
    title: 'Jenya Trading Platform',
    tagline: 'Real-time event-driven trading system with 17 Brokers Integration',
    description: 'Real-time event-driven trading platform with 17 broker integrations, supporting intraday trading, positions, holdings, and advanced reporting. Built using microservices architecture with Redis, Docker, and AWS for high performance and scalability.',
    category: ['MERN', 'React', 'Node.js', 'Mysql' ,'TypeScript', 'AWS', 'Microservices', 'Real-Time'],
    tech: ['React', 'Node.js', 'MySQL', 'WebSockets', 'Redis', 'Docker', 'AWS', 'Microservices', 'RBAC', 'JWT'],
    featured: true,
    status: 'Live',
    metrics: [
      { label: 'Latency', value: '<1s' },
      // { label: 'Concurrent Users', value: '10K+' },
      { label: 'Uptime', value: '99.9%' },
    ],
    accentColor: 'from-emerald-500/20 to-cyan-500/10',
    icon: '📈',
    media: [
      { type: 'image', url: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80', caption: 'Live Trading Dashboard' },
      // { type: 'image', url: 'https://images.unsplash.com/photo-1642790551116-18e150f248e3?w=800&q=80', caption: 'Market Data Feed' },
      // { type: 'image', url: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80', caption: 'Analytics View' },
    ],
  },
  {
    id: 2,
    title: 'Event-Driven E-Commerce Platform',
    tagline: 'Microservices + Kafka marketplace on AWS EKS',
    description:
      'Engineered a scalable microservices-based system using Node.js, Kafka (AWS MSK), and Kubernetes (EKS). Implemented event-driven architecture for order processing, inventory, and payments using Kafka streams. Deployed on AWS with API Gateway, Lambda, DynamoDB, and CI/CD pipelines ensuring high availability and fault tolerance.',
    category: ['MERN', 'React', 'Node.js', 'TypeScript', 'AWS', 'Microservices'],
    tech: ['React', 'Node.js', 'MySQL', 'Kafka', 'Kubernetes', 'AWS EKS', 'API Gateway', 'Lambda', 'DynamoDB', 'Docker', 'CI/CD'],
    featured: true,
    status: 'Live',
    metrics: [
      // { label: 'Orders/Day', value: '1K+' },
      { label: 'Services', value: '12+' },
      { label: 'Deploy Time', value: '-60%' },
    ],
    accentColor: 'from-amber-500/20 to-orange-500/10',
    icon: '🛒',
    media: [
      // { type: 'image', url: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=800&q=80', caption: 'Storefront UI' },
      // { type: 'image', url: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80', caption: 'Vendor Dashboard' },
      { type: 'image', url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80', caption: 'Order Analytics' },
    ],
  },
  {
    id: 3,
    title: 'Real-Time Analytics Dashboard',
    tagline: 'High-throughput streaming analytics with Kafka + WebSockets',
    description:
      'Built a high-throughput analytics system using Kafka, Node.js, and WebSockets to process and stream real-time data. Implemented event pipelines and Redis caching for low-latency updates. Deployed using Docker and Kubernetes with monitoring via CloudWatch and ELK/OpenSearch stack.',
    category: ['React', 'Node.js', 'TypeScript', 'AWS', 'Microservices', 'Real-Time'],
    tech: ['React', 'Node.js', 'Kafka', 'WebSockets', 'Redis', 'Docker', 'Kubernetes', 'CloudWatch', 'OpenSearch', 'ELK'],
    featured: true,
    status: 'Live',
    metrics: [
      // { label: 'Events/sec', value: '50K+' },
      { label: 'Latency', value: '<100ms' },
      { label: 'Data Sources', value: '20+' },
    ],
    accentColor: 'from-cyan-500/20 to-blue-500/10',
    icon: '📊',
    media: [
      { type: 'image', url: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80', caption: 'Real-Time Dashboard' },
      // { type: 'image', url: 'https://images.unsplash.com/photo-1543286386-713bdd548da4?w=800&q=80', caption: 'Event Pipeline Monitor' },
      // { type: 'image', url: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&q=80', caption: 'OpenSearch Logs' },
    ],
  },
  {
    id: 4,
    title: 'Jenya HRMS System',
    tagline: 'Cross-platform employee management with Electron + React',
    description:
      'Architected a cross-platform employee management system using React, Node.js, Electron, and MySQL improving HR operational efficiency by 55%. Developed scalable backend services with optimised REST APIs and database performance tuning. Implemented attendance, leave management, payroll, and RBAC-based access control with caching and query optimisation.',
    category: ['MERN', 'React', 'Node.js', 'TypeScript'],
    tech: ['React', 'Electron.js', 'Node.js', 'MySQL', 'REST APIs', 'Redis', 'RBAC', 'JWT'],
    featured: false,
    status: 'Live',
    metrics: [
      { label: 'Efficiency Gain', value: '+55%' },
      { label: 'Modules', value: '8+' },
    ],
    accentColor: 'from-violet-500/20 to-purple-500/10',
    icon: '👥',
    media: [
      {
        type: 'youtube',
        url: 'https://www.youtube.com/playlist?list=PLkaTl15B255crc2ojToWqWLyCrCg63MyE',
        caption: 'Full HRMS Demo Playlist',
        thumb: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
      },
      {
        type: 'youtube',
        url: 'https://www.youtube.com/playlist?list=PLkaTl15B255ee-B6BRQxk508s3JB9wKV-',
        caption: 'Full HRMS Demo Playlist',
        thumb: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
      },
      // { type: 'image', url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80', caption: 'Payroll Module' },
    ],
  },
  {
    id: 5,
    title: 'Distributed Job Processing System',
    tagline: 'Fault-tolerant queue-based async processing with Kubernetes',
    description:
      'Implemented a fault-tolerant background job processing system using SQS/Kafka, Node.js, and worker services. Added retry mechanisms, dead-letter queues, and horizontal scaling using Kubernetes. Improved processing efficiency and reliability for high-volume asynchronous tasks.',
    category: ['Node.js', 'TypeScript', 'AWS', 'Microservices'],
    tech: ['Node.js', 'TypeScript', 'Kafka', 'AWS SQS', 'Kubernetes', 'Docker', 'Redis', 'DLQ', 'PM2'],
    codeUrl: 'https://github.com/piyushbhai',
    featured: false,
    status: 'Live',
    metrics: [
      { label: 'Job Throughput', value: '+70%' },
      { label: 'Failure Rate', value: '<0.1%' },
    ],
    accentColor: 'from-rose-500/20 to-pink-500/10',
    icon: '⚙️',
    media: [
      { type: 'image', url: 'https://images.unsplash.com/photo-1518432031352-d6fc5c10da5a?w=800&q=80', caption: 'Job Queue Monitor' },
      { type: 'image', url: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80', caption: 'Worker Scaling View' },
    ],
  },
  {
    id: 6,
    title: 'Rayvat Chat System',
    tagline: 'High-performance real-time messaging with Socket.io + Redis like slack',
    description:
      'Architected a high-performance real-time communication system using React, Node.js, and Socket.io for low-latency bidirectional messaging at scale. Designed efficient WebSocket architecture with optimised event handling. Implemented RBAC, secure authentication, Redis for session management, and fault-tolerant design with structured logging.',
    category: ['MERN', 'React', 'Node.js', 'Real-Time'],
    tech: ['MERN', 'React', 'Node.js', 'Socket.io', 'Redis', 'JWT', 'RBAC', 'Docker', 'MongoDB', 'S3 Bucket',"AWS"],
    featured: false,
    status: 'Live',
    metrics: [
      { label: 'Messages/sec', value: '5K+' },
      { label: 'Active Rooms', value: '500+' },
    ],
    accentColor: 'from-teal-500/20 to-emerald-500/10',
    icon: '💬',
    media: [
      { type: 'image', url: 'https://images.unsplash.com/photo-1611746872915-64382b5c76da?w=800&q=80', caption: 'Chat Interface' },
      { type: 'image', url: 'https://images.unsplash.com/photo-1577563908411-5077b6dc7624?w=800&q=80', caption: 'Admin Console' },
    ],
  },
  {
    id: 7,
    title: 'Product Management App',
    tagline: 'Multi-tenant product catalog with Next.js SSR + PostgreSQL',
    description:
      'Architected a scalable multi-tenant product catalog using Next.js, Node.js, and PostgreSQL with multi-domain support and high-performance product management. Service-oriented architecture with optimised REST APIs, database indexing, SSR, and caching for performance and SEO. Deployed on AWS with Docker containerisation and seamless CI/CD.',
    category: ['MERN', 'React', 'Node.js', 'TypeScript', 'AWS'],
    tech: ['Next.js', 'TypeScript', 'Node.js', 'PostgreSQL', 'Docker', 'AWS', 'Redis', 'SSR', 'CI/CD'],
    featured: false,
    status: 'Live',
    metrics: [
      // { label: 'Tenants', value: '50+' },
      { label: 'Products', value: '100K+' },
    ],
    accentColor: 'from-indigo-500/20 to-blue-500/10',
    icon: '📦',
    media: [
      { type: 'image', url: 'https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=800&q=80', caption: 'Product Catalog' },
      { type: 'image', url: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&q=80', caption: 'Admin Panel' },
    ],
  },
  {
    id: 8,
    title: 'Dialdigits – VoIP Subscription Platform',
    tagline: 'Scalable VoIP management with Angular + CodeIgniter',
    description:
      'Developed a scalable VoIP subscription management platform using Angular and CodeIgniter, enabling efficient user onboarding, plan management, and billing workflows. Built optimised RESTful APIs with secure authentication, RBAC, and billing logic ensuring accurate subscription tracking.',
    category: ['React', 'Node.js'],
    tech: ['Angular', 'CodeIgniter', 'PHP', 'MySQL', 'REST APIs', 'RBAC', 'JWT'],
    featured: false,
    status: 'Live',
    metrics: [
      { label: 'Subscribers', value: '2K+' },
      { label: 'Uptime', value: '99.5%' },
    ],
    accentColor: 'from-sky-500/20 to-cyan-500/10',
    icon: '📞',
    media: [
       {
        type: 'youtube',
        url: 'https://www.youtube.com/playlist?list=PLkaTl15B255eWPOpIPISjsyvlwNQ06Z8D',
        caption: 'Billing Portal',
        thumb: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
      },
      // { type: 'image', url: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&q=80', caption: 'Subscription Dashboard' },
      // { type: 'image', url: 'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=800&q=80', caption: 'Billing Portal' },
    ],
  },
  {
    id: 9,
    title: 'Didbiz – VoIP Admin Platform',
    tagline: 'Full-stack VoIP admin with Stripe billing + React + Node.js',
    description:
      'Architected a scalable VoIP administration platform using React, Node.js, and MySQL supporting user management, subscription lifecycle, and billing operations. Integrated Stripe for secure automated payment processing and subscription management with full RBAC and audit logging.',
    category: ['MERN', 'React', 'Node.js'],
    tech: ['React', 'Node.js', 'MySQL', 'Stripe', 'REST APIs', 'RBAC', 'Docker'],
    featured: false,
    status: 'Live',
    metrics: [
      { label: 'Revenue Processed', value: '$500K+' },
      { label: 'API Response', value: '<150ms' },
    ],
    accentColor: 'from-orange-500/20 to-amber-500/10',
    icon: '🔌',
    media: [
      { type: 'image', url: 'https://images.unsplash.com/photo-1556155092-490a1ba16284?w=800&q=80', caption: 'Admin Dashboard' },
      { type: 'image', url: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&q=80', caption: 'Stripe Billing View' },
    ],
  },
];

const CATEGORIES: Category[] = ['All', 'MERN', 'React', 'Node.js', 'TypeScript', 'AWS', 'Microservices', 'Real-Time'];

const STATUS_COLORS: Record<Project['status'], string> = {
  Live: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  'In Progress': 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  Archived: 'text-slate-400 bg-slate-500/10 border-slate-500/30',
};

// ════════════════════════════════════════════════════════════════
//  SYSTEM DESIGN DATA  (table-driven)
// ════════════════════════════════════════════════════════════════

const SYSTEM_DESIGNS: SystemDesign[] = [
  {
    id: 1,
    title: 'Jenya Trading Platform',
    subtitle: 'Real-Time Event-Driven Architecture for managing 17 broker and handle reports',
    description: 'Real-time event-driven trading platform with 17 broker integrations, supporting intraday trading, positions, holdings, and advanced reporting. Built using microservices architecture with Redis, Docker, and AWS for high performance and scalability.',
    icon: '📈',
    accentColor: '#10b981',
    scale: '<1s latency · 99.9% uptime',
    overview: [
      'WebSocket-first architecture for real-time market feed delivery',
      'Microservices split by domain: auth, market-data, orders, portfolio',
      'Redis pub/sub for broadcasting price updates across server instances',
      'MySQL with read replicas for order history and reporting',
    ],
    steps: [
      { phase: '1. Client Layer',     component: 'React SPA',               technology: 'React + TypeScript + Redux Toolkit',  purpose: 'Real-time trading UI with live price charts and order book', scalability: 'CDN-distributed static assets',          notes: 'WebSocket connection maintained per authenticated session' },
      { phase: '2. API Gateway',      component: 'Load Balancer + Gateway',  technology: 'AWS ALB + API Gateway',               purpose: 'Route requests, enforce rate limiting, SSL termination',     scalability: 'Auto-scales with target tracking',       notes: 'JWT validated at gateway before routing' },
      { phase: '3. Auth Service',     component: 'Auth Microservice',        technology: 'Node.js + JWT + Redis',               purpose: 'Token issuance, refresh, RBAC enforcement',                 scalability: 'Stateless — scales horizontally',        notes: 'Session tokens stored in Redis with TTL' },
      { phase: '4. Market Data Feed', component: 'WebSocket Server',         technology: 'Node.js + Socket.io + Redis Pub/Sub', purpose: 'Stream live price ticks to subscribed clients',             scalability: 'Redis adapter for multi-instance fan-out', notes: 'Price updates pushed at sub-second intervals' },
      { phase: '5. Order Service',    component: 'Order Microservice',       technology: 'Node.js + MySQL + Redis',             purpose: 'Place, modify, cancel orders with idempotency',             scalability: 'Horizontal pods behind ALB',             notes: 'Optimistic locking prevents double-execution' },
      { phase: '6. Cache Layer',      component: 'Redis Cluster',            technology: 'AWS ElastiCache (Redis)',             purpose: 'Hot market data, session store, pub/sub broker',            scalability: 'Cluster mode with sharding',             notes: 'LRU eviction on market data keys' },
      { phase: '7. Database',         component: 'MySQL + Read Replicas',    technology: 'AWS RDS MySQL + Read Replicas',       purpose: 'Persistent order records, user data, trade history',        scalability: 'Read replicas for reporting queries',    notes: 'Write to primary, read analytics from replica' },
      { phase: '8. Infrastructure',   component: 'Docker + AWS',             technology: 'Docker + AWS EC2 + Auto Scaling',     purpose: 'Containerised deployment with auto-scaling groups',         scalability: 'ASG scales on CPU/connection metrics',  notes: 'CloudWatch alarms trigger scale-out events' },
    ],
    keyDecisions: [
      { decision: 'WebSocket over HTTP polling',  rationale: 'Eliminates ~200ms round-trip overhead per price update; enables true sub-second delivery at scale.' },
      { decision: 'Redis Pub/Sub for fan-out',    rationale: 'Decouples the market data producer from N WebSocket servers; avoids direct server-to-server coupling.' },
      { decision: 'Microservices per domain',     rationale: 'Allows independent scaling of order service under peak load without scaling the entire monolith.' },
    ],
    achievements: [
      'Sub-second market data delivery to 10K+ concurrent clients',
      'System performance improved by 40% using Redis caching',
      'Zero-downtime deployments with blue/green on AWS',
      'Fault-tolerant design with automatic failover',
    ],
  },
  {
    id: 2,
    title: 'Event-Driven E-Commerce (Microservices + Kafka)',
    subtitle: 'Kafka-Powered Order Processing on AWS EKS',
    description: 'Microservices architecture using Kafka event streams for order processing, inventory, and payments — deployed on Kubernetes EKS.',
    icon: '🛒',
    accentColor: '#f59e0b',
    scale: '12 microservices · 60% faster deploys',
    overview: [
      'Domain-driven microservices: catalog, order, inventory, payment, notification',
      'Kafka (AWS MSK) as the event bus connecting all services asynchronously',
      'Kubernetes (EKS) for container orchestration with horizontal pod autoscaling',
      'AWS Lambda for lightweight async handlers (email, webhooks)',
    ],
    steps: [
      { phase: '1. Frontend',          component: 'React Storefront',         technology: 'React + TypeScript + Next.js',         purpose: 'SSR product pages, CSR cart/checkout',                    scalability: 'Vercel/CloudFront edge caching',         notes: 'ISR for product catalog, CSR for dynamic cart' },
      { phase: '2. API Gateway',       component: 'AWS API Gateway',          technology: 'AWS API Gateway + Lambda Authorizer',  purpose: 'Unified entry point, JWT auth, rate limiting',             scalability: 'Managed by AWS — infinite scale',        notes: 'Lambda authorizer caches policy for 5 min' },
      { phase: '3. Order Service',     component: 'Order Microservice',       technology: 'Node.js + MySQL + Kafka Producer',     purpose: 'Accept orders, emit OrderPlaced events to Kafka',          scalability: 'Horizontal pods on EKS',                 notes: 'Idempotency key prevents duplicate orders' },
      { phase: '4. Kafka Event Bus',   component: 'Apache Kafka (MSK)',       technology: 'AWS MSK (Managed Kafka)',              purpose: 'Decouple services via async event streams',                 scalability: 'Partitioned topics per domain',           notes: 'Retention: 7 days; consumers use consumer groups' },
      { phase: '5. Inventory Service', component: 'Inventory Microservice',   technology: 'Node.js + DynamoDB + Kafka Consumer',  purpose: 'Reserve stock on OrderPlaced, release on cancellation',    scalability: 'DynamoDB auto-scaling',                  notes: 'Optimistic locking on stock reservation' },
      { phase: '6. Payment Service',   component: 'Payment Microservice',     technology: 'Node.js + Stripe + Kafka',             purpose: 'Charge customer, emit PaymentSuccess/Failed',               scalability: 'Stateless horizontal pods',               notes: 'Stripe webhook idempotency keys stored in Redis' },
      { phase: '7. Notification',      component: 'Lambda Functions',         technology: 'AWS Lambda + SES + SNS',               purpose: 'Send order confirmation, shipping updates via email/SMS',   scalability: 'Serverless — auto-scales to demand',     notes: 'Triggered by Kafka consumer via Lambda event source' },
      { phase: '8. Orchestration',     component: 'Kubernetes EKS',           technology: 'AWS EKS + HPA + GitHub Actions',       purpose: 'Container orchestration and CI/CD automation',              scalability: 'HPA scales pods on CPU/memory',           notes: 'GitHub Actions → ECR → EKS rolling deploy' },
    ],
    keyDecisions: [
      { decision: 'Kafka over REST for inter-service comms', rationale: 'Eliminates synchronous coupling; services can fail and recover independently by replaying Kafka offsets.' },
      { decision: 'DynamoDB for inventory',                  rationale: 'Single-digit ms latency for stock checks at checkout; auto-scaling handles flash sales without manual capacity planning.' },
      { decision: 'Lambda for notifications',                rationale: 'Notification volume is bursty — serverless avoids paying for idle containers during quiet periods.' },
    ],
    achievements: [
      'Deployment time reduced by 60% via AWS CI/CD automation',
      '12 independent microservices with zero shared-database coupling',
      'Event replay capability for audit and disaster recovery',
      'HPA automatically scaled pods 3× during peak load tests',
    ],
  },
  {
    id: 3,
    title: 'Real-Time Analytics Dashboard',
    subtitle: 'Kafka → WebSocket Streaming Pipeline',
    description: 'High-throughput analytics ingestion pipeline processing 50K+ events/second, delivered to browser clients in under 100ms via WebSockets.',
    icon: '📊',
    accentColor: '#06b6d4',
    scale: '50K+ events/sec · <100ms end-to-end · 20+ data sources',
    overview: [
      'Multi-source ingestion: IoT, API events, user activity, server metrics',
      'Kafka as the central event stream with topic-per-domain partitioning',
      'Node.js stream processors transform and aggregate data in-flight',
      'WebSocket delivery pushes aggregated metrics to dashboard in real time',
    ],
    steps: [
      { phase: '1. Data Sources',     component: 'Event Producers',          technology: 'REST / SDK / IoT Agents',              purpose: 'Push raw events from 20+ sources to Kafka topics',          scalability: 'Producer clients are stateless',          notes: 'Events include timestamp, source_id, payload JSON' },
      { phase: '2. Kafka Ingestion',  component: 'Apache Kafka Topics',      technology: 'AWS MSK — partitioned topics',         purpose: 'Buffer and fan-out high-volume event streams',              scalability: '32 partitions per topic',                 notes: '7-day retention enables replay for backfilling' },
      { phase: '3. Stream Processor', component: 'Node.js Workers',          technology: 'Node.js + KafkaJS + Redis Streams',    purpose: 'Aggregate, filter, and enrich events per time window',      scalability: 'Consumer group horizontal scaling',       notes: 'Tumbling 5s windows for dashboard metric updates' },
      { phase: '4. Cache Layer',      component: 'Redis',                    technology: 'AWS ElastiCache Redis',                purpose: 'Store latest aggregated metrics for WS broadcast',          scalability: 'Cluster sharding by metric key',          notes: 'TTL of 10s on each metric key to avoid stale data' },
      { phase: '5. WebSocket Server', component: 'Socket.io Server',         technology: 'Node.js + Socket.io + Redis Adapter',  purpose: 'Push metric updates to subscribed dashboard clients',       scalability: 'Multi-instance via Redis pub/sub',         notes: 'Clients subscribe to named metric channels' },
      { phase: '6. Dashboard UI',     component: 'React Dashboard',          technology: 'React + Recharts + TypeScript',        purpose: 'Render live charts, KPI cards, alert indicators',           scalability: 'Static SPA — CDN distributed',            notes: 'React useReducer manages WS message batching' },
      { phase: '7. Monitoring',       component: 'Observability Stack',      technology: 'CloudWatch + OpenSearch (ELK)',        purpose: 'Log aggregation, latency tracking, anomaly detection',      scalability: 'OpenSearch auto-scales index shards',     notes: 'P99 latency alert at >200ms triggers PagerDuty' },
    ],
    keyDecisions: [
      { decision: 'Tumbling windows vs sliding windows', rationale: 'Tumbling 5s windows halve CPU load vs sliding; acceptable for dashboard use where slight lag is invisible to users.' },
      { decision: 'Redis for latest-value store',        rationale: 'O(1) read for any metric key; WebSocket server reads Redis before broadcasting — avoids reprocessing Kafka on reconnect.' },
      { decision: 'OpenSearch over Prometheus for logs', rationale: 'Full-text search on structured JSON logs; dashboards built in Kibana without extra tooling.' },
    ],
    achievements: [
      'End-to-end latency under 100ms from event production to browser display',
      '50K+ events/second ingested without message loss',
      'Replay capability rebuilt 30 days of historical dashboards in minutes',
      'P99 latency monitoring with automated alerting via CloudWatch',
    ],
  },
];

// ════════════════════════════════════════════════════════════════
//  MEDIA MODAL  (images + YouTube in one unified modal)
// ════════════════════════════════════════════════════════════════

interface MediaModalProps {
  media: ProjectMedia[];
  initialIndex: number;
  projectTitle: string;
  onClose: () => void;
}

const MediaModal: React.FC<MediaModalProps> = ({ media, initialIndex, projectTitle, onClose }) => {
  const [current, setCurrent] = useState(initialIndex);

  const prev = useCallback(() => setCurrent((c) => (c - 1 + media.length) % media.length), [media.length]);
  const next = useCallback(() => setCurrent((c) => (c + 1) % media.length), [media.length]);

  const item = media[current];

  // Keyboard navigation
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [prev, next, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.94)', backdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full rounded-2xl overflow-hidden bg-[#0e0e18] border border-slate-700 shadow-2xl"
        style={{ maxWidth: 900 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Modal header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <div className="flex items-center gap-3 min-w-0">
            {/* Type badge */}
            <span className={`shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono font-semibold border ${
              item.type === 'youtube'
                ? 'bg-red-500/10 border-red-500/30 text-red-400'
                : 'bg-slate-700/40 border-slate-600 text-slate-300'
            }`}>
              {item.type === 'youtube' ? (
                <>
                  <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
                    <path d="M23.495 6.205a3.007 3.007 0 00-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 00.527 6.205a31.247 31.247 0 00-.522 5.805 31.247 31.247 0 00.522 5.783 3.007 3.007 0 002.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 002.088-2.088 31.247 31.247 0 00.5-5.783 31.247 31.247 0 00-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/>
                  </svg>
                  YouTube
                </>
              ) : '🖼 Image'}
            </span>
            <div className="min-w-0">
              <div className="text-white font-bold text-sm truncate">{projectTitle}</div>
              <div className="text-slate-500 text-xs font-mono truncate">{item.caption}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0 ml-4">
            <span className="text-slate-500 text-xs font-mono">{current + 1} / {media.length}</span>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors text-xl leading-none"
            >×</button>
          </div>
        </div>

        {/* ── Main media area ── */}
        <div className="relative bg-slate-950" style={{ aspectRatio: '16/9' }}>
          {item.type === 'youtube' ? (
            /* ── YouTube embed ── */
            <iframe
              key={current} /* force re-mount on slide change so autoplay works */
              src={getYouTubeEmbedUrl(item.url)}
              title={item.caption}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              style={{ border: 'none' }}
            />
          ) : (
            /* ── Image ── */
            <img
              src={item.url}
              alt={item.caption}
              className="w-full h-full object-cover"
            />
          )}

          {/* Prev / Next arrows — shown only when >1 item */}
          {media.length > 1 && (
            <>
              <button
                onClick={prev}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/70 hover:bg-black/90 text-white text-xl transition-all hover:scale-110 select-none"
              >‹</button>
              <button
                onClick={next}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/70 hover:bg-black/90 text-white text-xl transition-all hover:scale-110 select-none"
              >›</button>
            </>
          )}
        </div>

        {/* ── Thumbnail strip ── */}
        {media.length > 1 && (
          <div className="flex gap-2 px-5 py-4 overflow-x-auto bg-[#0a0a12] border-t border-slate-800">
            {media.map((m, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`relative shrink-0 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                  i === current ? 'border-emerald-400 opacity-100 scale-105' : 'border-slate-700 opacity-50 hover:opacity-80'
                }`}
                style={{ width: 72, height: 48 }}
                title={m.caption}
              >
                <img
                  src={mediaThumbnail(m)}
                  alt={m.caption}
                  className="w-full h-full object-cover"
                />
                {/* YouTube play overlay on thumbnail */}
                {m.type === 'youtube' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <div className="w-5 h-5 rounded-full bg-red-600 flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 fill-white" viewBox="0 0 8 8">
                        <polygon points="2,1 7,4 2,7" />
                      </svg>
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Keyboard hint */}
        <div className="px-5 py-2 bg-[#0a0a12] border-t border-slate-800/60 flex items-center justify-between">
          <span className="text-xs text-slate-600 font-mono">Press ← → to navigate · Esc to close</span>
          {item.type === 'youtube' && (
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-red-400 hover:text-red-300 font-mono transition-colors flex items-center gap-1"
            >
              <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
                <path d="M23.495 6.205a3.007 3.007 0 00-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 00.527 6.205a31.247 31.247 0 00-.522 5.805 31.247 31.247 0 00.522 5.783 3.007 3.007 0 002.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 002.088-2.088 31.247 31.247 0 00.5-5.783 31.247 31.247 0 00-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/>
              </svg>
              Open on YouTube ↗
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
//  MEDIA TYPE BADGE  (small inline indicator)
// ════════════════════════════════════════════════════════════════

const MediaTypeBadge: React.FC<{ media: ProjectMedia[] }> = ({ media }) => {
  const hasYT    = media.some((m) => m.type === 'youtube');
  const hasImage = media.some((m) => m.type === 'image');

  return (
    <div className="flex items-center gap-1.5">
      {hasYT && (
        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/25 text-red-400 text-xs font-mono">
          <svg className="w-2.5 h-2.5 fill-current" viewBox="0 0 24 24">
            <path d="M23.495 6.205a3.007 3.007 0 00-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 00.527 6.205a31.247 31.247 0 00-.522 5.805 31.247 31.247 0 00.522 5.783 3.007 3.007 0 002.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 002.088-2.088 31.247 31.247 0 00.5-5.783 31.247 31.247 0 00-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/>
          </svg>
          Demo
        </span>
      )}
      {hasImage && (
        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-700/40 border border-slate-600/40 text-slate-400 text-xs font-mono">
          🖼 {media.filter((m) => m.type === 'image').length}
        </span>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
//  PROJECT CARDS
// ════════════════════════════════════════════════════════════════

interface FeaturedCardProps {
  project: Project;
  onOpen: (project: Project, index: number) => void;
}

const FeaturedCard: React.FC<FeaturedCardProps> = ({ project, onOpen }) => {
  const coverMedia = project.media[0];
  const coverThumb = mediaThumbnail(coverMedia);

  return (
    <div className="relative rounded-2xl bg-[#13131e] border border-slate-800 overflow-hidden group hover:border-slate-600 transition-all duration-300">
      <div className={`absolute inset-0 bg-gradient-to-br ${project.accentColor} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} />

      {/* ── Cover media ── */}
      <div
        className="relative w-full overflow-hidden cursor-pointer"
        style={{ height: 200 }}
        onClick={() => onOpen(project, 0)}
      >
        <img
          src={coverThumb}
          alt={coverMedia.caption}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#13131e] via-transparent to-transparent" />

        {/* Featured badge */}
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-1 rounded-full bg-slate-900/80 border border-slate-700 text-xs font-mono text-slate-300 backdrop-blur-sm">★ Featured</span>
        </div>

        {/* YouTube play overlay on cover if first item is YT */}
        {coverMedia.type === 'youtube' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-red-600/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200">
              <svg className="w-6 h-6 fill-white ml-1" viewBox="0 0 8 8">
                <polygon points="2,1 7,4 2,7" />
              </svg>
            </div>
          </div>
        )}

        {/* Thumbnail strip */}
        <div className="absolute bottom-3 right-3 flex gap-1.5">
          {project.media.slice(0, 4).map((m, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); onOpen(project, i); }}
              className="relative w-10 h-10 rounded-lg overflow-hidden border border-white/20 hover:border-emerald-400 transition-colors"
              title={m.caption}
            >
              <img src={mediaThumbnail(m)} alt={m.caption} className="w-full h-full object-cover" />
              {m.type === 'youtube' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <div className="w-4 h-4 rounded-full bg-red-600 flex items-center justify-center">
                    <svg className="w-2 h-2 fill-white" viewBox="0 0 8 8"><polygon points="2,1 7,4 2,7" /></svg>
                  </div>
                </div>
              )}
            </button>
          ))}
          {project.media.length > 4 && (
            <div className="w-10 h-10 rounded-lg bg-black/60 border border-white/10 flex items-center justify-center text-white text-xs font-mono">
              +{project.media.length - 4}
            </div>
          )}
        </div>
      </div>

      {/* ── Card body ── */}
      <div className="relative z-10 p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{project.icon}</span>
            <span className={`px-2.5 py-0.5 rounded-full border text-xs font-mono ${STATUS_COLORS[project.status]}`}>
              {project.status === 'Live' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5 animate-pulse" />}
              {project.status}
            </span>
          </div>
          <MediaTypeBadge media={project.media} />
        </div>

        <h3 className="text-xl font-black text-white mb-1.5 group-hover:text-emerald-100 transition-colors">{project.title}</h3>
        <p className="text-emerald-400 font-semibold text-xs mb-3">{project.tagline}</p>
        <p className="text-slate-400 text-sm leading-relaxed mb-5">{project.description}</p>

        {project.metrics && (
          <div className="flex flex-wrap gap-5 mb-5 pb-4 border-b border-slate-800">
            {project.metrics.map((m) => (
              <div key={m.label}>
                <div className="text-lg font-black text-white">{m.value}</div>
                <div className="text-xs text-slate-500 font-mono">{m.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-1.5 mb-5">
          {project.tech.map((t) => (
            <span key={t} className="px-2 py-1 text-xs bg-slate-900/80 border border-slate-800 text-slate-400 rounded-md font-mono">{t}</span>
          ))}
        </div>

        <div className="flex flex-wrap gap-3">
          {project.liveUrl && (
            <a href={project.liveUrl} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm transition-all duration-200 hover:shadow-[0_0_20px_rgba(16,185,129,0.35)]">
              ↗ Live Demo
            </a>
          )}
          {project.codeUrl && (
            <a href={project.codeUrl} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-slate-700 hover:border-slate-500 text-slate-300 hover:text-white font-semibold text-sm transition-all duration-200">
              &lt;/&gt; Code
            </a>
          )}
          <button
            onClick={() => onOpen(project, 0)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-700 hover:border-slate-500 text-slate-400 hover:text-white font-semibold text-sm transition-all duration-200"
          >
            {project.media.some((m) => m.type === 'youtube') ? (
              <><svg className="w-3.5 h-3.5 fill-red-400" viewBox="0 0 24 24"><path d="M23.495 6.205a3.007 3.007 0 00-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 00.527 6.205a31.247 31.247 0 00-.522 5.805 31.247 31.247 0 00.522 5.783 3.007 3.007 0 002.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 002.088-2.088 31.247 31.247 0 00.5-5.783 31.247 31.247 0 00-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/></svg> Watch Demo</>
            ) : (
              <>🖼 Gallery</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

interface ProjectCardProps {
  project: Project;
  onOpen: (project: Project, index: number) => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onOpen }) => {
  const coverMedia = project.media[0];
  const coverThumb = mediaThumbnail(coverMedia);
  const hasYT = project.media.some((m) => m.type === 'youtube');

  return (
    <div className="relative flex flex-col rounded-xl bg-[#13131e] border border-slate-800 overflow-hidden group hover:border-slate-600 transition-all duration-300 hover:-translate-y-1">
      <div className={`h-0.5 w-full bg-gradient-to-r ${project.accentColor.replace('/20', '').replace('/10', '')} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
      <div className={`absolute inset-0 bg-gradient-to-br ${project.accentColor} opacity-0 group-hover:opacity-60 transition-opacity duration-500 pointer-events-none`} />

      {/* Cover */}
      <div
        className="relative w-full overflow-hidden cursor-pointer"
        style={{ height: 140 }}
        onClick={() => onOpen(project, 0)}
      >
        <img src={coverThumb} alt={coverMedia.caption}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#13131e] via-transparent to-transparent" />

        {/* YouTube play icon on thumbnail */}
        {coverMedia.type === 'youtube' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-red-600/85 flex items-center justify-center shadow group-hover:scale-110 transition-transform duration-200">
              <svg className="w-4 h-4 fill-white ml-0.5" viewBox="0 0 8 8">
                <polygon points="2,1 7,4 2,7" />
              </svg>
            </div>
          </div>
        )}

        {/* Media count badge on hover */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <MediaTypeBadge media={project.media} />
        </div>
      </div>

      <div className="relative z-10 p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xl">{project.icon}</span>
            <span className={`px-2 py-0.5 rounded-full border text-xs font-mono ${STATUS_COLORS[project.status]}`}>
              {project.status === 'Live' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5 animate-pulse" />}
              {project.status}
            </span>
          </div>
          <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            {project.liveUrl && (
              <a href={project.liveUrl} target="_blank" rel="noreferrer"
                className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-800 hover:bg-emerald-500/20 text-slate-400 hover:text-emerald-400 transition-all text-sm">↗</a>
            )}
            {project.codeUrl && (
              <a href={project.codeUrl} target="_blank" rel="noreferrer"
                className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-all text-xs font-mono">{'</>'}</a>
            )}
            {/* Watch / Gallery quick-open */}
            <button
              onClick={() => onOpen(project, 0)}
              className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all text-xs ${
                hasYT
                  ? 'bg-red-500/15 hover:bg-red-500/25 text-red-400'
                  : 'bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white'
              }`}
              title={hasYT ? 'Watch Demo' : 'View Gallery'}
            >
              {hasYT ? (
                <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                  <path d="M23.495 6.205a3.007 3.007 0 00-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 00.527 6.205a31.247 31.247 0 00-.522 5.805 31.247 31.247 0 00.522 5.783 3.007 3.007 0 002.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 002.088-2.088 31.247 31.247 0 00.5-5.783 31.247 31.247 0 00-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/>
                </svg>
              ) : '🖼'}
            </button>
          </div>
        </div>

        <h3 className="text-sm font-bold text-white mb-1 group-hover:text-emerald-100 transition-colors leading-snug">{project.title}</h3>
        <p className="text-xs text-emerald-400 font-semibold mb-2">{project.tagline}</p>
        <p className="text-xs text-slate-400 leading-relaxed flex-1 mb-4">{project.description}</p>

        {project.metrics && (
          <div className="flex gap-4 mb-3 pb-3 border-b border-slate-800">
            {project.metrics.slice(0, 2).map((m) => (
              <div key={m.label}>
                <div className="text-sm font-black text-white">{m.value}</div>
                <div className="text-xs text-slate-500 font-mono">{m.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-1">
          {project.tech.slice(0, 5).map((t) => (
            <span key={t} className="px-1.5 py-0.5 text-xs bg-slate-900 border border-slate-800 text-slate-500 rounded font-mono">{t}</span>
          ))}
          {project.tech.length > 5 && <span className="px-1.5 py-0.5 text-xs text-slate-600 font-mono">+{project.tech.length - 5}</span>}
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
//  SYSTEM DESIGN SECTION  (table layout)
// ════════════════════════════════════════════════════════════════

const SystemDesignSection: React.FC = () => {
  const [activeId,  setActiveId]  = useState(1);
  const [activeTab, setActiveTab] = useState<'table' | 'decisions' | 'achievements'>('table');

  const design = SYSTEM_DESIGNS.find((d) => d.id === activeId)!;

  return (
    <div id="system-design" className="mt-24 pt-16 border-t border-slate-800/60">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
        <div>
          <p className="text-cyan-400 font-mono text-sm tracking-widest uppercase mb-3">System Design Lens</p>
          <h3 className="text-3xl md:text-4xl font-black text-white">Architecture Breakdowns</h3>
          <p className="text-slate-400 mt-2 max-w-xl text-sm leading-relaxed">
            Step-by-step architectural decisions — component, technology, purpose, and scalability strategy for each layer.
          </p>
        </div>
        <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-[#0d1520] border border-cyan-500/20 text-xs font-mono">
          <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
          <span className="text-cyan-400">{SYSTEM_DESIGNS.length} systems documented</span>
        </div>
      </div>

      {/* Selector cards */}
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        {SYSTEM_DESIGNS.map((d) => (
          <button key={d.id} onClick={() => { setActiveId(d.id); setActiveTab('table'); }}
            className={`text-left p-5 rounded-xl border transition-all duration-200 ${
              activeId === d.id ? 'border-slate-600 bg-[#13131e]' : 'border-slate-800 bg-[#0e0e18] hover:border-slate-700'
            }`}>
            <div className="h-0.5 w-full rounded-full mb-4 transition-opacity duration-200"
              style={{ background: d.accentColor, opacity: activeId === d.id ? 1 : 0.2 }} />
            <div className="text-2xl mb-2">{d.icon}</div>
            <div className="font-bold text-white text-sm leading-snug mb-1.5">{d.title}</div>
            <div className="text-xs font-mono" style={{ color: activeId === d.id ? d.accentColor : '#475569' }}>{d.scale}</div>
          </button>
        ))}
      </div>

      <div className="rounded-2xl bg-[#0e0e18] border border-slate-800 overflow-hidden">
        {/* Card header */}
        <div className="px-8 py-6 border-b border-slate-800"
          style={{ background: `linear-gradient(135deg, ${design.accentColor}12 0%, transparent 55%)` }}>
          <div className="flex flex-wrap items-start gap-4">
            <span className="text-3xl mt-0.5">{design.icon}</span>
            <div className="flex-1 min-w-0">
              <h4 className="text-xl font-black text-white">{design.title}</h4>
              <p className="text-sm font-semibold mt-1" style={{ color: design.accentColor }}>{design.subtitle}</p>
              <p className="text-sm text-slate-400 leading-relaxed mt-2 max-w-3xl">{design.description}</p>
            </div>
            <div className="shrink-0 px-3 py-1.5 rounded-lg border text-xs font-mono"
              style={{ borderColor: `${design.accentColor}40`, background: `${design.accentColor}0d`, color: design.accentColor }}>
              {design.scale}
            </div>
          </div>
          <div className="mt-5 grid sm:grid-cols-2 gap-2">
            {design.overview.map((o, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-slate-400">
                <span className="mt-1 shrink-0" style={{ color: design.accentColor }}>▸</span>{o}
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-800 bg-[#0a0a12]">
          {[
            { key: 'table' as const,        label: '📋  Architecture Table' },
            { key: 'decisions' as const,    label: '⚖️  Key Decisions'       },
            { key: 'achievements' as const, label: '🏆  Achievements'        },
          ].map((tab) => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`shrink-0 px-6 py-3.5 text-sm font-semibold transition-all duration-150 border-b-2 whitespace-nowrap ${
                activeTab === tab.key ? 'text-white' : 'border-transparent text-slate-500 hover:text-slate-300'
              }`}
              style={activeTab === tab.key ? { borderColor: design.accentColor, color: design.accentColor } : {}}>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          {activeTab === 'table' && (
            <table className="w-full text-sm border-collapse" style={{ minWidth: 800 }}>
              <thead>
                <tr className="bg-[#0a0a12] border-b border-slate-800">
                  {['Phase', 'Component', 'Technology', 'Purpose', 'Scalability', 'Notes'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-500 whitespace-nowrap border-r border-slate-800/60 last:border-r-0">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {design.steps.map((step, i) => (
                  <tr key={i} className={`border-b border-slate-800/60 transition-colors duration-150 hover:bg-slate-800/20 ${i % 2 === 0 ? 'bg-transparent' : 'bg-slate-900/20'}`}>
                    <td className="px-4 py-3.5 border-r border-slate-800/60 align-top">
                      <span className="px-2 py-1 rounded-md text-xs font-black font-mono whitespace-nowrap"
                        style={{ background: `${design.accentColor}18`, color: design.accentColor }}>{step.phase}</span>
                    </td>
                    <td className="px-4 py-3.5 border-r border-slate-800/60 align-top">
                      <span className="font-bold text-white text-xs whitespace-nowrap">{step.component}</span>
                    </td>
                    <td className="px-4 py-3.5 border-r border-slate-800/60 align-top">
                      <span className="text-cyan-300 font-mono text-xs">{step.technology}</span>
                    </td>
                    <td className="px-4 py-3.5 border-r border-slate-800/60 align-top">
                      <span className="text-slate-300 text-xs leading-relaxed">{step.purpose}</span>
                    </td>
                    <td className="px-4 py-3.5 border-r border-slate-800/60 align-top">
                      <span className="text-emerald-400 text-xs font-mono">{step.scalability}</span>
                    </td>
                    <td className="px-4 py-3.5 align-top">
                      <span className="text-slate-500 text-xs italic leading-relaxed">{step.notes}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {activeTab === 'decisions' && (
            <div className="p-8 space-y-5">
              {design.keyDecisions.map((dec, i) => (
                <div key={i} className="rounded-xl bg-[#13131e] border border-slate-800 overflow-hidden">
                  <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-800">
                    <span className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black font-mono shrink-0"
                      style={{ background: `${design.accentColor}20`, color: design.accentColor, border: `1px solid ${design.accentColor}30` }}>{i + 1}</span>
                    <span className="font-bold text-white text-sm">{dec.decision}</span>
                  </div>
                  <div className="px-5 py-4">
                    <div className="text-xs font-mono text-slate-500 uppercase tracking-wider mb-2">Rationale</div>
                    <p className="text-sm text-slate-300 leading-relaxed">{dec.rationale}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'achievements' && (
            <div className="p-8">
              <div className="grid sm:grid-cols-2 gap-4">
                {design.achievements.map((a, i) => (
                  <div key={i} className="flex items-start gap-4 p-5 rounded-xl bg-[#13131e] border border-slate-800 hover:border-emerald-500/20 transition-colors">
                    <div className="w-9 h-9 shrink-0 rounded-xl flex items-center justify-center text-lg"
                      style={{ background: `${design.accentColor}18`, border: `1px solid ${design.accentColor}25` }}>🏆</div>
                    <p className="text-sm text-slate-300 leading-relaxed pt-1">{a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
//  MAIN EXPORT
// ════════════════════════════════════════════════════════════════

export const ProjectsSection: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<Category>('All');
  const [modal, setModal] = useState<{ project: Project; index: number } | null>(null);

  const filtered = useMemo(
    () => activeFilter === 'All' ? PROJECTS : PROJECTS.filter((p) => p.category.includes(activeFilter)),
    [activeFilter]
  );

  const featured = filtered.filter((p) => p.featured);
  const rest     = filtered.filter((p) => !p.featured);

  const openModal  = useCallback((project: Project, index: number) => setModal({ project, index }), []);
  const closeModal = useCallback(() => setModal(null), []);

  return (
    <>
      {modal && (
        <MediaModal
          media={modal.project.media}
          initialIndex={modal.index}
          projectTitle={modal.project.title}
          onClose={closeModal}
        />
      )}

      <section id="portfolio" className="py-28 bg-[#0a0a0f]">
        <div className="max-w-6xl mx-auto px-6">

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <p className="text-emerald-400 font-mono text-sm tracking-widest uppercase mb-3">Work</p>
              <h2 className="text-4xl md:text-5xl font-black text-white">Projects</h2>
              <p className="text-slate-400 mt-3 max-w-xl">
                Production systems built across trading, e-commerce, HRMS, VOIP, and real-time domains — 9.5+ years of shipped code.
              </p>
            </div>
            <div className="flex gap-6 shrink-0">
              {[
                { value: `${PROJECTS.length}`, label: 'Projects' },
                { value: `${PROJECTS.filter((p) => p.status === 'Live').length}`, label: 'Live' },
                { value: '10K+', label: 'Users Served' },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-2xl font-black text-emerald-400">{s.value}</div>
                  <div className="text-xs text-slate-500 font-mono uppercase tracking-wider">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-12">
            {CATEGORIES.map((cat) => {
              const count = cat === 'All' ? PROJECTS.length : PROJECTS.filter((p) => p.category.includes(cat)).length;
              return (
                <button key={cat} onClick={() => setActiveFilter(cat)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-semibold transition-all duration-200 ${
                    activeFilter === cat
                      ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300'
                      : 'bg-[#13131e] border-slate-800 text-slate-400 hover:border-slate-600 hover:text-slate-200'
                  }`}>
                  {cat}
                  <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${activeFilter === cat ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-600'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <div className="text-5xl mb-4">🔍</div>
              <p className="font-mono text-slate-600">No projects in this category.</p>
            </div>
          )}

          {featured.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {featured.map((p) => <FeaturedCard key={p.id} project={p} onOpen={openModal} />)}
            </div>
          )}

          {rest.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {rest.map((p) => <ProjectCard key={p.id} project={p} onOpen={openModal} />)}
            </div>
          )}

          {/* GitHub/GitLab CTA */}
          <div className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 rounded-2xl bg-[#13131e] border border-slate-800">
            <div>
              <div className="font-bold text-white">Want to see more?</div>
              <div className="text-sm text-slate-400">Full source code and open-source work on GitHub and GitLab.</div>
            </div>
            <div className="flex gap-3 shrink-0">
              <a href="https://github.com/piyushbhai" target="_blank" rel="noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-slate-700 hover:border-emerald-500/40 text-slate-300 hover:text-emerald-400 font-semibold text-sm transition-all duration-200">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/></svg>
                GitHub
              </a>
              <a href="https://gitlab.com/piyushprajapati/myportfolio" target="_blank" rel="noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-slate-700 hover:border-orange-500/40 text-slate-300 hover:text-orange-400 font-semibold text-sm transition-all duration-200">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M4.845.904C4.605.904 4.4 1.088 4.354 1.32L.014 17.88c-.06.25.04.51.26.645l11.741 7.456a.51.51 0 00.54 0l11.741-7.456c.22-.135.32-.395.26-.645L20.216 1.32C20.17 1.088 19.965.904 19.725.904h-4.46L13.5 10.48 12 5.97l-1.5 4.51L8.735.904H4.845z"/></svg>
                GitLab
              </a>
            </div>
          </div>

          <SystemDesignSection />
        </div>
      </section>
    </>
  );
};

export default ProjectsSection;


// import React, { useState, useMemo, useCallback } from 'react';

// // ════════════════════════════════════════════════════════════════
// //  TYPES
// // ════════════════════════════════════════════════════════════════

// type Category = 'All' | 'MERN' | 'React' | 'Node.js' | 'TypeScript' | 'AWS' | 'Microservices' | 'Real-Time';

// interface ProjectImage {
//   url: string;
//   caption: string;
// }

// interface Project {
//   id: number;
//   title: string;
//   tagline: string;
//   description: string;
//   category: Category[];
//   tech: string[];
//   liveUrl?: string;
//   codeUrl?: string;
//   featured: boolean;
//   status: 'Live' | 'In Progress' | 'Archived';
//   metrics?: { label: string; value: string }[];
//   accentColor: string;
//   icon: string;
//   images: ProjectImage[];
// }

// interface DesignStep {
//   phase: string;
//   component: string;
//   technology: string;
//   purpose: string;
//   scalability: string;
//   notes: string;
// }

// interface SystemDesign {
//   id: number;
//   title: string;
//   subtitle: string;
//   description: string;
//   icon: string;
//   accentColor: string;
//   scale: string;
//   overview: string[];
//   steps: DesignStep[];
//   keyDecisions: { decision: string; rationale: string }[];
//   achievements: string[];
// }

// // ════════════════════════════════════════════════════════════════
// //  PROJECTS DATA  (from CV — real projects)
// // ════════════════════════════════════════════════════════════════

// const PROJECTS: Project[] = [
//   {
//     id: 1,
//     title: 'Jenya Trading Platform',
//     tagline: 'Real-time event-driven trading system with sub-second market data',
//     description:
//       'Architected a real-time, event-driven trading platform processing high-frequency market data via WebSockets with sub-second latency. Built scalable microservices backend with Redis caching, containerized on Docker, and deployed on AWS with auto-scaling and fault-tolerant design. Implemented RBAC, secure authentication, and API protection.',
//     category: ['MERN', 'React', 'Node.js', 'TypeScript', 'AWS', 'Microservices', 'Real-Time'],
//     tech: ['React', 'Node.js', 'MySQL', 'WebSockets', 'Redis', 'Docker', 'AWS', 'Microservices', 'RBAC', 'JWT'],
//     // liveUrl: 'https://jenya.in',
//     featured: true,
//     status: 'Live',
//     metrics: [
//       { label: 'Latency', value: '<1s' },
//       { label: 'Concurrent Users', value: '10K+' },
//       { label: 'Uptime', value: '99.9%' },
//     ],
//     accentColor: 'from-emerald-500/20 to-cyan-500/10',
//     icon: '📈',
//     images: [
//       { url: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80', caption: 'Live Trading Dashboard' },
//       { url: 'https://images.unsplash.com/photo-1642790551116-18e150f248e3?w=800&q=80', caption: 'Market Data Feed' },
//       { url: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80', caption: 'Analytics View' },
//     ],
//   },
//   {
//     id: 2,
//     title: 'Event-Driven E-Commerce Platform',
//     tagline: 'Microservices + Kafka marketplace on AWS EKS',
//     description:
//       'Engineered a scalable microservices-based system using Node.js, Kafka (AWS MSK), and Kubernetes (EKS). Implemented event-driven architecture for order processing, inventory, and payments using Kafka streams. Deployed on AWS with API Gateway, Lambda, DynamoDB, and CI/CD pipelines ensuring high availability and fault tolerance.',
//     category: ['MERN', 'React', 'Node.js', 'TypeScript', 'AWS', 'Microservices'],
//     tech: ['React', 'Node.js', 'MySQL', 'Kafka', 'Kubernetes', 'AWS EKS', 'API Gateway', 'Lambda', 'DynamoDB', 'Docker', 'CI/CD'],
//     featured: true,
//     status: 'Live',
//     metrics: [
//       { label: 'Orders/Day', value: '1K+' },
//       { label: 'Services', value: '12+' },
//       { label: 'Deploy Time', value: '-60%' },
//     ],
//     accentColor: 'from-amber-500/20 to-orange-500/10',
//     icon: '🛒',
//     images: [
//       { url: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=800&q=80', caption: 'Storefront UI' },
//       { url: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80', caption: 'Vendor Dashboard' },
//       { url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80', caption: 'Order Analytics' },
//     ],
//   },
//   {
//     id: 3,
//     title: 'Real-Time Analytics Dashboard',
//     tagline: 'High-throughput streaming analytics with Kafka + WebSockets',
//     description:
//       'Built a high-throughput analytics system using Kafka, Node.js, and WebSockets to process and stream real-time data. Implemented event pipelines and Redis caching for low-latency updates. Deployed using Docker and Kubernetes with monitoring via CloudWatch and ELK/OpenSearch stack.',
//     category: ['React', 'Node.js', 'TypeScript', 'AWS', 'Microservices', 'Real-Time'],
//     tech: ['React', 'Node.js', 'Kafka', 'WebSockets', 'Redis', 'Docker', 'Kubernetes', 'CloudWatch', 'OpenSearch', 'ELK'],
//     featured: true,
//     status: 'Live',
//     metrics: [
//       { label: 'Events/sec', value: '50K+' },
//       { label: 'Latency', value: '<100ms' },
//       { label: 'Data Sources', value: '20+' },
//     ],
//     accentColor: 'from-cyan-500/20 to-blue-500/10',
//     icon: '📊',
//     images: [
//       { url: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80', caption: 'Real-Time Dashboard' },
//       { url: 'https://images.unsplash.com/photo-1543286386-713bdd548da4?w=800&q=80', caption: 'Event Pipeline Monitor' },
//       { url: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&q=80', caption: 'OpenSearch Logs' },
//     ],
//   },
//   {
//     id: 4,
//     title: 'Jenya HRMS System',
//     tagline: 'Cross-platform employee management with Electron + React',
//     description:
//       'Architected a cross-platform employee management system using React, Node.js, Electron, and MySQL improving HR operational efficiency by 55%. Developed scalable backend services with optimised REST APIs and database performance tuning. Implemented attendance, leave management, payroll, and RBAC-based access control with caching and query optimisation.',
//     category: ['MERN', 'React', 'Node.js', 'TypeScript'],
//     tech: ['React', 'Electron.js', 'Node.js', 'MySQL', 'REST APIs', 'Redis', 'RBAC', 'JWT'],
//     featured: false,
//     status: 'Live',
//     metrics: [
//       { label: 'Efficiency Gain', value: '+55%' },
//       { label: 'Modules', value: '8+' },
//     ],
//     accentColor: 'from-violet-500/20 to-purple-500/10',
//     icon: '👥',
//     images: [
//       { url: 'https://www.youtube.com/playlist?list=PLkaTl15B255crc2ojToWqWLyCrCg63MyE', caption: 'Employee Dashboard' },
//       { url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80', caption: 'Payroll Management' },
//     ],
//   },
//   {
//     id: 5,
//     title: 'Distributed Job Processing System',
//     tagline: 'Fault-tolerant queue-based async processing with Kubernetes',
//     description:
//       'Implemented a fault-tolerant background job processing system using SQS/Kafka, Node.js, and worker services. Added retry mechanisms, dead-letter queues, and horizontal scaling using Kubernetes. Improved processing efficiency and reliability for high-volume asynchronous tasks.',
//     category: ['Node.js', 'TypeScript', 'AWS', 'Microservices'],
//     tech: ['Node.js', 'TypeScript', 'Kafka', 'AWS SQS', 'Kubernetes', 'Docker', 'Redis', 'DLQ', 'PM2'],
//     codeUrl: 'https://github.com/piyushbhai',
//     featured: false,
//     status: 'Live',
//     metrics: [
//       { label: 'Job Throughput', value: '+70%' },
//       { label: 'Failure Rate', value: '<0.1%' },
//     ],
//     accentColor: 'from-rose-500/20 to-pink-500/10',
//     icon: '⚙️',
//     images: [
//       { url: 'https://images.unsplash.com/photo-1518432031352-d6fc5c10da5a?w=800&q=80', caption: 'Job Queue Monitor' },
//       { url: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80', caption: 'Worker Scaling View' },
//     ],
//   },
//   {
//     id: 6,
//     title: 'Rayvat Chat System',
//     tagline: 'High-performance real-time messaging with Socket.io + Redis',
//     description:
//       'Architected a high-performance real-time communication system using React, Node.js, and Socket.io for low-latency bidirectional messaging at scale. Designed efficient WebSocket architecture with optimised event handling. Implemented RBAC, secure authentication, Redis for session management, and fault-tolerant design with structured logging.',
//     category: ['MERN', 'React', 'Node.js', 'Real-Time'],
//     tech: ['React', 'Node.js', 'Socket.io', 'Redis', 'JWT', 'RBAC', 'Docker', 'MongoDB'],
//     featured: false,
//     status: 'Live',
//     metrics: [
//       { label: 'Messages/sec', value: '5K+' },
//       { label: 'Active Rooms', value: '500+' },
//     ],
//     accentColor: 'from-teal-500/20 to-emerald-500/10',
//     icon: '💬',
//     images: [
//       { url: 'https://images.unsplash.com/photo-1611746872915-64382b5c76da?w=800&q=80', caption: 'Chat Interface' },
//       { url: 'https://images.unsplash.com/photo-1577563908411-5077b6dc7624?w=800&q=80', caption: 'Admin Console' },
//     ],
//   },
//   {
//     id: 7,
//     title: 'Product Management App',
//     tagline: 'Multi-tenant product catalog with Next.js SSR + PostgreSQL',
//     description:
//       'Architected a scalable multi-tenant product catalog using Next.js, Node.js, and PostgreSQL with multi-domain support and high-performance product management. Service-oriented architecture with optimised REST APIs, database indexing, SSR, and caching for performance and SEO. Deployed on AWS with Docker containerisation and seamless CI/CD.',
//     category: ['MERN', 'React', 'Node.js', 'TypeScript', 'AWS'],
//     tech: ['Next.js', 'TypeScript', 'Node.js', 'PostgreSQL', 'Docker', 'AWS', 'Redis', 'SSR', 'CI/CD'],
//     featured: false,
//     status: 'Live',
//     metrics: [
//       { label: 'Tenants', value: '50+' },
//       { label: 'Products', value: '100K+' },
//     ],
//     accentColor: 'from-indigo-500/20 to-blue-500/10',
//     icon: '📦',
//     images: [
//       { url: 'https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=800&q=80', caption: 'Product Catalog' },
//       { url: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&q=80', caption: 'Admin Panel' },
//     ],
//   },
//   {
//     id: 8,
//     title: 'Dialdigits – VoIP Subscription Platform',
//     tagline: 'Scalable VoIP management with Angular + CodeIgniter',
//     description:
//       'Developed a scalable VoIP subscription management platform using Angular and CodeIgniter, enabling efficient user onboarding, plan management, and billing workflows. Built optimised RESTful APIs with secure authentication, RBAC, and billing logic ensuring accurate subscription tracking.',
//     category: ['React', 'Node.js'],
//     tech: ['Angular', 'CodeIgniter', 'PHP', 'MySQL', 'REST APIs', 'RBAC', 'JWT'],
//     featured: false,
//     status: 'Live',
//     metrics: [
//       { label: 'Subscribers', value: '2K+' },
//       { label: 'Uptime', value: '99.5%' },
//     ],
//     accentColor: 'from-sky-500/20 to-cyan-500/10',
//     icon: '📞',
//     images: [
//       { url: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&q=80', caption: 'Subscription Dashboard' },
//       { url: 'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=800&q=80', caption: 'Billing Portal' },
//     ],
//   },
//   {
//     id: 9,
//     title: 'Didbiz – VoIP Admin Platform',
//     tagline: 'Full-stack VoIP admin with Stripe billing + React + Node.js',
//     description:
//       'Architected a scalable VoIP administration platform using React, Node.js, and MySQL supporting user management, subscription lifecycle, and billing operations. Integrated Stripe for secure automated payment processing and subscription management with full RBAC and audit logging.',
//     category: ['MERN', 'React', 'Node.js'],
//     tech: ['React', 'Node.js', 'MySQL', 'Stripe', 'REST APIs', 'RBAC', 'Docker'],
//     featured: false,
//     status: 'Live',
//     metrics: [
//       { label: 'Revenue Processed', value: '$500K+' },
//       { label: 'API Response', value: '<150ms' },
//     ],
//     accentColor: 'from-orange-500/20 to-amber-500/10',
//     icon: '🔌',
//     images: [
//       { url: 'https://images.unsplash.com/photo-1556155092-490a1ba16284?w=800&q=80', caption: 'Admin Dashboard' },
//       { url: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&q=80', caption: 'Stripe Billing View' },
//     ],
//   },
// ];

// const CATEGORIES: Category[] = ['All', 'MERN', 'React', 'Node.js', 'TypeScript', 'AWS', 'Microservices', 'Real-Time'];

// const STATUS_COLORS: Record<Project['status'], string> = {
//   Live: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
//   'In Progress': 'text-amber-400 bg-amber-500/10 border-amber-500/30',
//   Archived: 'text-slate-400 bg-slate-500/10 border-slate-500/30',
// };

// // ════════════════════════════════════════════════════════════════
// //  SYSTEM DESIGN DATA  (table-driven)
// // ════════════════════════════════════════════════════════════════

// const SYSTEM_DESIGNS: SystemDesign[] = [
//   {
//     id: 1,
//     title: 'Jenya Trading Platform',
//     subtitle: 'Real-Time Event-Driven Architecture',
//     description: 'How the trading platform processes high-frequency market data with sub-second latency across 10K+ concurrent users.',
//     icon: '📈',
//     accentColor: '#10b981',
//     scale: '10K+ users · <1s latency · 99.9% uptime',
//     overview: [
//       'WebSocket-first architecture for real-time market feed delivery',
//       'Microservices split by domain: auth, market-data, orders, portfolio',
//       'Redis pub/sub for broadcasting price updates across server instances',
//       'MySQL with read replicas for order history and reporting',
//     ],
//     steps: [
//       { phase: '1. Client Layer',      component: 'React SPA',            technology: 'React + TypeScript + Redux Toolkit', purpose: 'Real-time trading UI with live price charts and order book', scalability: 'CDN-distributed static assets', notes: 'WebSocket connection maintained per authenticated session' },
//       { phase: '2. API Gateway',       component: 'Load Balancer + Gateway', technology: 'AWS ALB + API Gateway',           purpose: 'Route requests, enforce rate limiting, SSL termination',   scalability: 'Auto-scales with target tracking',  notes: 'JWT validated at gateway before routing' },
//       { phase: '3. Auth Service',      component: 'Auth Microservice',    technology: 'Node.js + JWT + Redis',              purpose: 'Token issuance, refresh, RBAC enforcement',               scalability: 'Stateless — scales horizontally',  notes: 'Session tokens stored in Redis with TTL' },
//       { phase: '4. Market Data Feed',  component: 'WebSocket Server',     technology: 'Node.js + Socket.io + Redis Pub/Sub', purpose: 'Stream live price ticks to subscribed clients',           scalability: 'Redis adapter for multi-instance fan-out', notes: 'Price updates pushed at sub-second intervals' },
//       { phase: '5. Order Service',     component: 'Order Microservice',   technology: 'Node.js + MySQL + Redis',            purpose: 'Place, modify, cancel orders with idempotency',           scalability: 'Horizontal pods behind ALB',        notes: 'Optimistic locking prevents double-execution' },
//       { phase: '6. Cache Layer',       component: 'Redis Cluster',        technology: 'AWS ElastiCache (Redis)',            purpose: 'Hot market data, session store, pub/sub broker',          scalability: 'Cluster mode with sharding',        notes: 'LRU eviction on market data keys' },
//       { phase: '7. Database',          component: 'MySQL + Read Replicas', technology: 'AWS RDS MySQL + Read Replicas',     purpose: 'Persistent order records, user data, trade history',      scalability: 'Read replicas for reporting queries', notes: 'Write to primary, read analytics from replica' },
//       { phase: '8. Infrastructure',    component: 'Docker + AWS',         technology: 'Docker + AWS EC2 + Auto Scaling',    purpose: 'Containerised deployment with auto-scaling groups',       scalability: 'ASG scales on CPU/connection metrics', notes: 'CloudWatch alarms trigger scale-out events' },
//     ],
//     keyDecisions: [
//       { decision: 'WebSocket over HTTP polling', rationale: 'Eliminates ~200ms round-trip overhead per price update; enables true sub-second delivery at scale.' },
//       { decision: 'Redis Pub/Sub for fan-out',   rationale: 'Decouples the market data producer from N WebSocket servers; avoids direct server-to-server coupling.' },
//       { decision: 'Microservices per domain',    rationale: 'Allows independent scaling of order service under peak load without scaling the entire monolith.' },
//     ],
//     achievements: [
//       'Sub-second market data delivery to 10K+ concurrent clients',
//       'System performance improved by 40% using Redis caching',
//       'Zero-downtime deployments with blue/green on AWS',
//       'Fault-tolerant design with automatic failover',
//     ],
//   },
//   {
//     id: 2,
//     title: 'Event-Driven E-Commerce (Microservices + Kafka)',
//     subtitle: 'Kafka-Powered Order Processing on AWS EKS',
//     description: 'Microservices architecture using Kafka event streams for order processing, inventory, and payments — deployed on Kubernetes EKS.',
//     icon: '🛒',
//     accentColor: '#f59e0b',
//     scale: '1K+ orders/day · 12 microservices · 60% faster deploys',
//     overview: [
//       'Domain-driven microservices: catalog, order, inventory, payment, notification',
//       'Kafka (AWS MSK) as the event bus connecting all services asynchronously',
//       'Kubernetes (EKS) for container orchestration with horizontal pod autoscaling',
//       'AWS Lambda for lightweight async handlers (email, webhooks)',
//     ],
//     steps: [
//       { phase: '1. Frontend',         component: 'React Storefront',      technology: 'React + TypeScript + Next.js',        purpose: 'SSR product pages, CSR cart/checkout',                   scalability: 'Vercel/CloudFront edge caching',     notes: 'ISR for product catalog, CSR for dynamic cart' },
//       { phase: '2. API Gateway',      component: 'AWS API Gateway',       technology: 'AWS API Gateway + Lambda Authorizer', purpose: 'Unified entry point, JWT auth, rate limiting',            scalability: 'Managed by AWS — infinite scale',   notes: 'Lambda authorizer caches policy for 5 min' },
//       { phase: '3. Order Service',    component: 'Order Microservice',    technology: 'Node.js + MySQL + Kafka Producer',    purpose: 'Accept orders, emit OrderPlaced events to Kafka',        scalability: 'Horizontal pods on EKS',            notes: 'Idempotency key prevents duplicate orders' },
//       { phase: '4. Kafka Event Bus',  component: 'Apache Kafka (MSK)',    technology: 'AWS MSK (Managed Kafka)',             purpose: 'Decouple services via async event streams',               scalability: 'Partitioned topics per domain',     notes: 'Retention: 7 days; consumers use consumer groups' },
//       { phase: '5. Inventory Service', component: 'Inventory Microservice', technology: 'Node.js + DynamoDB + Kafka Consumer', purpose: 'Reserve stock on OrderPlaced, release on cancellation', scalability: 'DynamoDB auto-scaling',             notes: 'Optimistic locking on stock reservation' },
//       { phase: '6. Payment Service',  component: 'Payment Microservice',  technology: 'Node.js + Stripe + Kafka',            purpose: 'Charge customer, emit PaymentSuccess/Failed',            scalability: 'Stateless horizontal pods',         notes: 'Stripe webhook idempotency keys stored in Redis' },
//       { phase: '7. Notification',     component: 'Lambda Functions',      technology: 'AWS Lambda + SES + SNS',              purpose: 'Send order confirmation, shipping updates via email/SMS', scalability: 'Serverless — auto-scales to demand', notes: 'Triggered by Kafka consumer via Lambda event source' },
//       { phase: '8. Orchestration',    component: 'Kubernetes EKS',        technology: 'AWS EKS + HPA + GitHub Actions',      purpose: 'Container orchestration and CI/CD automation',           scalability: 'HPA scales pods on CPU/memory',     notes: 'GitHub Actions → ECR → EKS rolling deploy' },
//     ],
//     keyDecisions: [
//       { decision: 'Kafka over REST for inter-service comms', rationale: 'Eliminates synchronous coupling; services can fail and recover independently by replaying Kafka offsets.' },
//       { decision: 'DynamoDB for inventory', rationale: 'Single-digit ms latency for stock checks at checkout; auto-scaling handles flash sales without manual capacity planning.' },
//       { decision: 'Lambda for notifications', rationale: 'Notification volume is bursty — serverless avoids paying for idle containers during quiet periods.' },
//     ],
//     achievements: [
//       'Deployment time reduced by 60% via AWS CI/CD automation',
//       '12 independent microservices with zero shared-database coupling',
//       'Event replay capability for audit and disaster recovery',
//       'HPA automatically scaled pods 3× during peak load tests',
//     ],
//   },
//   {
//     id: 3,
//     title: 'Real-Time Analytics Dashboard',
//     subtitle: 'Kafka → WebSocket Streaming Pipeline',
//     description: 'High-throughput analytics ingestion pipeline processing 50K+ events/second, delivered to browser clients in under 100ms via WebSockets.',
//     icon: '📊',
//     accentColor: '#06b6d4',
//     scale: '50K+ events/sec · <100ms end-to-end · 20+ data sources',
//     overview: [
//       'Multi-source ingestion: IoT, API events, user activity, server metrics',
//       'Kafka as the central event stream with topic-per-domain partitioning',
//       'Node.js stream processors transform and aggregate data in-flight',
//       'WebSocket delivery pushes aggregated metrics to dashboard in real time',
//     ],
//     steps: [
//       { phase: '1. Data Sources',     component: 'Event Producers',        technology: 'REST / SDK / IoT Agents',            purpose: 'Push raw events from 20+ sources to Kafka topics',       scalability: 'Producer clients are stateless',    notes: 'Events include timestamp, source_id, payload JSON' },
//       { phase: '2. Kafka Ingestion',  component: 'Apache Kafka Topics',    technology: 'AWS MSK — partitioned topics',        purpose: 'Buffer and fan-out high-volume event streams',           scalability: '32 partitions per topic',           notes: '7-day retention enables replay for backfilling' },
//       { phase: '3. Stream Processor', component: 'Node.js Workers',        technology: 'Node.js + KafkaJS + Redis Streams',  purpose: 'Aggregate, filter, and enrich events per time window',   scalability: 'Consumer group horizontal scaling',  notes: 'Tumbling 5s windows for dashboard metric updates' },
//       { phase: '4. Cache Layer',      component: 'Redis',                  technology: 'AWS ElastiCache Redis',               purpose: 'Store latest aggregated metrics for WS broadcast',       scalability: 'Cluster sharding by metric key',    notes: 'TTL of 10s on each metric key to avoid stale data' },
//       { phase: '5. WebSocket Server', component: 'Socket.io Server',       technology: 'Node.js + Socket.io + Redis Adapter', purpose: 'Push metric updates to subscribed dashboard clients',    scalability: 'Multi-instance via Redis pub/sub',  notes: 'Clients subscribe to named metric channels' },
//       { phase: '6. Dashboard UI',     component: 'React Dashboard',        technology: 'React + Recharts + TypeScript',       purpose: 'Render live charts, KPI cards, alert indicators',        scalability: 'Static SPA — CDN distributed',      notes: 'React useReducer manages WS message batching' },
//       { phase: '7. Monitoring',       component: 'Observability Stack',    technology: 'CloudWatch + OpenSearch (ELK)',       purpose: 'Log aggregation, latency tracking, anomaly detection',   scalability: 'OpenSearch auto-scales index shards', notes: 'P99 latency alert at >200ms triggers PagerDuty' },
//     ],
//     keyDecisions: [
//       { decision: 'Tumbling windows vs sliding windows', rationale: 'Tumbling 5s windows halve CPU load vs sliding; acceptable for dashboard use where slight lag is invisible to users.' },
//       { decision: 'Redis for latest-value store', rationale: 'O(1) read for any metric key; WebSocket server reads Redis before broadcasting — avoids reprocessing Kafka on reconnect.' },
//       { decision: 'OpenSearch over Prometheus for logs', rationale: 'Full-text search on structured JSON logs; dashboards built in Kibana without extra tooling.' },
//     ],
//     achievements: [
//       'End-to-end latency under 100ms from event production to browser display',
//       '50K+ events/second ingested without message loss',
//       'Replay capability rebuilt 30 days of historical dashboards in minutes',
//       'P99 latency monitoring with automated alerting via CloudWatch',
//     ],
//   },
// ];

// // ════════════════════════════════════════════════════════════════
// //  IMAGE GALLERY MODAL
// // ════════════════════════════════════════════════════════════════

// interface GalleryModalProps {
//   images: ProjectImage[];
//   initialIndex: number;
//   projectTitle: string;
//   onClose: () => void;
// }

// const GalleryModal: React.FC<GalleryModalProps> = ({ images, initialIndex, projectTitle, onClose }) => {
//   const [current, setCurrent] = useState(initialIndex);

//   const prev = useCallback(() => setCurrent((c) => (c - 1 + images.length) % images.length), [images.length]);
//   const next = useCallback(() => setCurrent((c) => (c + 1) % images.length), [images.length]);

//   return (
//     <div
//       className="fixed inset-0 z-50 flex items-center justify-center p-4"
//       style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(8px)' }}
//       onClick={onClose}
//     >
//       <div
//         className="relative max-w-4xl w-full rounded-2xl overflow-hidden bg-[#0e0e18] border border-slate-700"
//         onClick={(e) => e.stopPropagation()}
//       >
//         {/* Header */}
//         <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
//           <div>
//             <div className="text-white font-bold text-sm">{projectTitle}</div>
//             <div className="text-slate-500 text-xs font-mono mt-0.5">{images[current].caption}</div>
//           </div>
//           <div className="flex items-center gap-3">
//             <span className="text-slate-500 text-xs font-mono">{current + 1} / {images.length}</span>
//             <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors text-lg">×</button>
//           </div>
//         </div>

//         {/* Image */}
//         <div className="relative aspect-video bg-slate-950 overflow-hidden">
//           <img
//             src={images[current].url}
//             alt={images[current].caption}
//             className="w-full h-full object-cover"
//           />
//           {/* Nav arrows */}
//           {images.length > 1 && (
//             <>
//               <button
//                 onClick={prev}
//                 className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/60 hover:bg-black/80 text-white text-lg transition-all hover:scale-110"
//               >‹</button>
//               <button
//                 onClick={next}
//                 className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/60 hover:bg-black/80 text-white text-lg transition-all hover:scale-110"
//               >›</button>
//             </>
//           )}
//         </div>

//         {/* Thumbnails */}
//         {images.length > 1 && (
//           <div className="flex gap-2 px-6 py-4 overflow-x-auto">
//             {images.map((img, i) => (
//               <button
//                 key={i}
//                 onClick={() => setCurrent(i)}
//                 className={`shrink-0 rounded-lg overflow-hidden border-2 transition-all duration-200 ${i === current ? 'border-emerald-400 opacity-100' : 'border-slate-700 opacity-50 hover:opacity-80'}`}
//                 style={{ width: 72, height: 48 }}
//               >
//                 <img src={img.url} alt={img.caption} className="w-full h-full object-cover" />
//               </button>
//             ))}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// // ════════════════════════════════════════════════════════════════
// //  PROJECT CARDS
// // ════════════════════════════════════════════════════════════════

// interface FeaturedCardProps {
//   project: Project;
//   onOpenGallery: (project: Project, index: number) => void;
// }

// const FeaturedCard: React.FC<FeaturedCardProps> = ({ project, onOpenGallery }) => (
//   <div className={`relative rounded-2xl bg-[#13131e] border border-slate-800 overflow-hidden group hover:border-slate-600 transition-all duration-300`}>
//     <div className={`absolute inset-0 bg-gradient-to-br ${project.accentColor} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} />

//     {/* Image strip */}
//     <div
//       className="relative w-full overflow-hidden cursor-pointer"
//       style={{ height: 200 }}
//       onClick={() => onOpenGallery(project, 0)}
//     >
//       <img
//         src={project.images[0].url}
//         alt={project.images[0].caption}
//         className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
//       />
//       {/* Overlay */}
//       <div className="absolute inset-0 bg-gradient-to-t from-[#13131e] via-transparent to-transparent" />

//       {/* Gallery button */}
//       <div className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 text-white text-xs font-mono">
//         <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
//           <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
//         </svg>
//         {project.images.length} photos
//       </div>

//       {/* Thumbnails strip */}
//       <div className="absolute bottom-3 left-3 flex gap-1.5">
//         {project.images.slice(0, 3).map((img, i) => (
//           <button
//             key={i}
//             onClick={(e) => { e.stopPropagation(); onOpenGallery(project, i); }}
//             className="w-10 h-10 rounded-lg overflow-hidden border border-white/20 hover:border-emerald-400 transition-colors"
//           >
//             <img src={img.url} alt={img.caption} className="w-full h-full object-cover" />
//           </button>
//         ))}
//       </div>

//       {/* Featured badge */}
//       <div className="absolute top-3 left-3">
//         <span className="px-2.5 py-1 rounded-full bg-slate-900/80 border border-slate-700 text-xs font-mono text-slate-300 backdrop-blur-sm">★ Featured</span>
//       </div>
//     </div>

//     <div className="relative z-10 p-6">
//       <div className="flex items-center gap-3 mb-4">
//         <span className="text-3xl">{project.icon}</span>
//         <span className={`px-2.5 py-0.5 rounded-full border text-xs font-mono ${STATUS_COLORS[project.status]}`}>
//           {project.status === 'Live' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5 animate-pulse" />}
//           {project.status}
//         </span>
//       </div>

//       <h3 className="text-xl font-black text-white mb-1.5 group-hover:text-emerald-100 transition-colors">{project.title}</h3>
//       <p className="text-emerald-400 font-semibold text-xs mb-3">{project.tagline}</p>
//       <p className="text-slate-400 text-sm leading-relaxed mb-5">{project.description}</p>

//       {project.metrics && (
//         <div className="flex flex-wrap gap-5 mb-5 pb-4 border-b border-slate-800">
//           {project.metrics.map((m) => (
//             <div key={m.label}>
//               <div className="text-lg font-black text-white">{m.value}</div>
//               <div className="text-xs text-slate-500 font-mono">{m.label}</div>
//             </div>
//           ))}
//         </div>
//       )}

//       <div className="flex flex-wrap gap-1.5 mb-5">
//         {project.tech.map((t) => (
//           <span key={t} className="px-2 py-1 text-xs bg-slate-900/80 border border-slate-800 text-slate-400 rounded-md font-mono">{t}</span>
//         ))}
//       </div>

//       <div className="flex gap-3">
//         {project.liveUrl && (
//           <a href={project.liveUrl} target="_blank" rel="noreferrer"
//             className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm transition-all duration-200 hover:shadow-[0_0_20px_rgba(16,185,129,0.35)]">
//             ↗ Live Demo
//           </a>
//         )}
//         {project.codeUrl && (
//           <a href={project.codeUrl} target="_blank" rel="noreferrer"
//             className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-slate-700 hover:border-slate-500 text-slate-300 hover:text-white font-semibold text-sm transition-all duration-200">
//             &lt;/&gt; Code
//           </a>
//         )}
//         <button
//           onClick={() => onOpenGallery(project, 0)}
//           className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-700 hover:border-slate-500 text-slate-400 hover:text-white font-semibold text-sm transition-all duration-200"
//         >
//           🖼 Gallery
//         </button>
//       </div>
//     </div>
//   </div>
// );

// interface ProjectCardProps {
//   project: Project;
//   onOpenGallery: (project: Project, index: number) => void;
// }

// const ProjectCard: React.FC<ProjectCardProps> = ({ project, onOpenGallery }) => (
//   <div className="relative flex flex-col rounded-xl bg-[#13131e] border border-slate-800 overflow-hidden group hover:border-slate-600 transition-all duration-300 hover:-translate-y-1">
//     <div className={`h-0.5 w-full bg-gradient-to-r ${project.accentColor.replace('/20', '').replace('/10', '')} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
//     <div className={`absolute inset-0 bg-gradient-to-br ${project.accentColor} opacity-0 group-hover:opacity-60 transition-opacity duration-500 pointer-events-none`} />

//     {/* Image */}
//     <div
//       className="relative w-full overflow-hidden cursor-pointer"
//       style={{ height: 140 }}
//       onClick={() => onOpenGallery(project, 0)}
//     >
//       <img src={project.images[0].url} alt={project.images[0].caption}
//         className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
//       <div className="absolute inset-0 bg-gradient-to-t from-[#13131e] via-transparent to-transparent" />
//       <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm text-white text-xs font-mono opacity-0 group-hover:opacity-100 transition-opacity">
//         🖼 {project.images.length}
//       </div>
//     </div>

//     <div className="relative z-10 p-5 flex flex-col flex-1">
//       <div className="flex items-start justify-between gap-2 mb-3">
//         <div className="flex items-center gap-2">
//           <span className="text-xl">{project.icon}</span>
//           <span className={`px-2 py-0.5 rounded-full border text-xs font-mono ${STATUS_COLORS[project.status]}`}>
//             {project.status === 'Live' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5 animate-pulse" />}
//             {project.status}
//           </span>
//         </div>
//         <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
//           {project.liveUrl && (
//             <a href={project.liveUrl} target="_blank" rel="noreferrer"
//               className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-800 hover:bg-emerald-500/20 text-slate-400 hover:text-emerald-400 transition-all text-sm">↗</a>
//           )}
//           {project.codeUrl && (
//             <a href={project.codeUrl} target="_blank" rel="noreferrer"
//               className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-all text-xs font-mono">{'</>'}</a>
//           )}
//         </div>
//       </div>

//       <h3 className="text-sm font-bold text-white mb-1 group-hover:text-emerald-100 transition-colors leading-snug">{project.title}</h3>
//       <p className="text-xs text-emerald-400 font-semibold mb-2">{project.tagline}</p>
//       <p className="text-xs text-slate-400 leading-relaxed flex-1 mb-4">{project.description}</p>

//       {project.metrics && (
//         <div className="flex gap-4 mb-3 pb-3 border-b border-slate-800">
//           {project.metrics.slice(0, 2).map((m) => (
//             <div key={m.label}>
//               <div className="text-sm font-black text-white">{m.value}</div>
//               <div className="text-xs text-slate-500 font-mono">{m.label}</div>
//             </div>
//           ))}
//         </div>
//       )}

//       <div className="flex flex-wrap gap-1">
//         {project.tech.slice(0, 5).map((t) => (
//           <span key={t} className="px-1.5 py-0.5 text-xs bg-slate-900 border border-slate-800 text-slate-500 rounded font-mono">{t}</span>
//         ))}
//         {project.tech.length > 5 && <span className="px-1.5 py-0.5 text-xs text-slate-600 font-mono">+{project.tech.length - 5}</span>}
//       </div>
//     </div>
//   </div>
// );

// // ════════════════════════════════════════════════════════════════
// //  SYSTEM DESIGN SECTION  (table layout)
// // ════════════════════════════════════════════════════════════════

// const SystemDesignSection: React.FC = () => {
//   const [activeId,  setActiveId]  = useState(1);
//   const [activeTab, setActiveTab] = useState<'table' | 'decisions' | 'achievements'>('table');

//   const design = SYSTEM_DESIGNS.find((d) => d.id === activeId)!;

//   return (
//     <div id="system-design" className="mt-24 pt-16 border-t border-slate-800/60">

//       {/* Header */}
//       <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
//         <div>
//           <p className="text-cyan-400 font-mono text-sm tracking-widest uppercase mb-3">System Design Lens</p>
//           <h3 className="text-3xl md:text-4xl font-black text-white">Architecture Breakdowns</h3>
//           <p className="text-slate-400 mt-2 max-w-xl text-sm leading-relaxed">
//             Step-by-step architectural decisions — component, technology, purpose, and scalability strategy for each layer.
//           </p>
//         </div>
//         <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-[#0d1520] border border-cyan-500/20 text-xs font-mono">
//           <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
//           <span className="text-cyan-400">{SYSTEM_DESIGNS.length} systems documented</span>
//         </div>
//       </div>

//       {/* Design selector */}
//       <div className="grid sm:grid-cols-3 gap-4 mb-8">
//         {SYSTEM_DESIGNS.map((d) => (
//           <button
//             key={d.id}
//             onClick={() => { setActiveId(d.id); setActiveTab('table'); }}
//             className={`text-left p-5 rounded-xl border transition-all duration-200 ${
//               activeId === d.id ? 'border-slate-600 bg-[#13131e]' : 'border-slate-800 bg-[#0e0e18] hover:border-slate-700'
//             }`}
//           >
//             <div className="h-0.5 w-full rounded-full mb-4 transition-opacity duration-200"
//               style={{ background: d.accentColor, opacity: activeId === d.id ? 1 : 0.2 }} />
//             <div className="text-2xl mb-2">{d.icon}</div>
//             <div className="font-bold text-white text-sm leading-snug mb-1.5">{d.title}</div>
//             <div className="text-xs font-mono transition-colors duration-200"
//               style={{ color: activeId === d.id ? d.accentColor : '#475569' }}>
//               {d.scale}
//             </div>
//           </button>
//         ))}
//       </div>

//       {/* Main card */}
//       <div className="rounded-2xl bg-[#0e0e18] border border-slate-800 overflow-hidden">

//         {/* Card header */}
//         <div className="px-8 py-6 border-b border-slate-800"
//           style={{ background: `linear-gradient(135deg, ${design.accentColor}12 0%, transparent 55%)` }}>
//           <div className="flex flex-wrap items-start gap-4">
//             <span className="text-3xl mt-0.5">{design.icon}</span>
//             <div className="flex-1 min-w-0">
//               <h4 className="text-xl font-black text-white">{design.title}</h4>
//               <p className="text-sm font-semibold mt-1" style={{ color: design.accentColor }}>{design.subtitle}</p>
//               <p className="text-sm text-slate-400 leading-relaxed mt-2 max-w-3xl">{design.description}</p>
//             </div>
//             <div className="shrink-0 px-3 py-1.5 rounded-lg border text-xs font-mono"
//               style={{ borderColor: `${design.accentColor}40`, background: `${design.accentColor}0d`, color: design.accentColor }}>
//               {design.scale}
//             </div>
//           </div>

//           {/* Overview bullets */}
//           <div className="mt-5 grid sm:grid-cols-2 gap-2">
//             {design.overview.map((o, i) => (
//               <div key={i} className="flex items-start gap-2 text-sm text-slate-400">
//                 <span className="mt-1 shrink-0" style={{ color: design.accentColor }}>▸</span>
//                 {o}
//               </div>
//             ))}
//           </div>
//         </div>

//         {/* Tab bar */}
//         <div className="flex border-b border-slate-800 bg-[#0a0a12]">
//           {[
//             { key: 'table' as const,       label: '📋  Architecture Table' },
//             { key: 'decisions' as const,   label: '⚖️  Key Decisions'       },
//             { key: 'achievements' as const, label: '🏆  Achievements'        },
//           ].map((tab) => (
//             <button key={tab.key} onClick={() => setActiveTab(tab.key)}
//               className={`shrink-0 px-6 py-3.5 text-sm font-semibold transition-all duration-150 border-b-2 whitespace-nowrap ${
//                 activeTab === tab.key ? 'text-white' : 'border-transparent text-slate-500 hover:text-slate-300'
//               }`}
//               style={activeTab === tab.key ? { borderColor: design.accentColor, color: design.accentColor } : {}}>
//               {tab.label}
//             </button>
//           ))}
//         </div>

//         {/* Tab content */}
//         <div className="overflow-x-auto">

//           {/* ── ARCHITECTURE TABLE ── */}
//           {activeTab === 'table' && (
//             <table className="w-full text-sm border-collapse" style={{ minWidth: 800 }}>
//               <thead>
//                 <tr className="bg-[#0a0a12] border-b border-slate-800">
//                   {['Phase', 'Component', 'Technology', 'Purpose', 'Scalability', 'Notes'].map((h) => (
//                     <th key={h} className="text-left px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-500 whitespace-nowrap border-r border-slate-800/60 last:border-r-0">
//                       {h}
//                     </th>
//                   ))}
//                 </tr>
//               </thead>
//               <tbody>
//                 {design.steps.map((step, i) => (
//                   <tr
//                     key={i}
//                     className={`border-b border-slate-800/60 transition-colors duration-150 hover:bg-slate-800/20 ${
//                       i % 2 === 0 ? 'bg-transparent' : 'bg-slate-900/20'
//                     }`}
//                   >
//                     {/* Phase */}
//                     <td className="px-4 py-3.5 border-r border-slate-800/60 align-top">
//                       <span className="px-2 py-1 rounded-md text-xs font-black font-mono whitespace-nowrap"
//                         style={{ background: `${design.accentColor}18`, color: design.accentColor }}>
//                         {step.phase}
//                       </span>
//                     </td>
//                     {/* Component */}
//                     <td className="px-4 py-3.5 border-r border-slate-800/60 align-top">
//                       <span className="font-bold text-white text-xs whitespace-nowrap">{step.component}</span>
//                     </td>
//                     {/* Technology */}
//                     <td className="px-4 py-3.5 border-r border-slate-800/60 align-top">
//                       <span className="text-cyan-300 font-mono text-xs">{step.technology}</span>
//                     </td>
//                     {/* Purpose */}
//                     <td className="px-4 py-3.5 border-r border-slate-800/60 align-top">
//                       <span className="text-slate-300 text-xs leading-relaxed">{step.purpose}</span>
//                     </td>
//                     {/* Scalability */}
//                     <td className="px-4 py-3.5 border-r border-slate-800/60 align-top">
//                       <span className="text-emerald-400 text-xs font-mono">{step.scalability}</span>
//                     </td>
//                     {/* Notes */}
//                     <td className="px-4 py-3.5 align-top">
//                       <span className="text-slate-500 text-xs italic leading-relaxed">{step.notes}</span>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           )}

//           {/* ── KEY DECISIONS ── */}
//           {activeTab === 'decisions' && (
//             <div className="p-8 space-y-5">
//               {design.keyDecisions.map((dec, i) => (
//                 <div key={i} className="rounded-xl bg-[#13131e] border border-slate-800 overflow-hidden">
//                   <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-800">
//                     <span className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black font-mono shrink-0"
//                       style={{ background: `${design.accentColor}20`, color: design.accentColor, border: `1px solid ${design.accentColor}30` }}>
//                       {i + 1}
//                     </span>
//                     <span className="font-bold text-white text-sm">{dec.decision}</span>
//                   </div>
//                   <div className="px-5 py-4">
//                     <div className="text-xs font-mono text-slate-500 uppercase tracking-wider mb-2">Rationale</div>
//                     <p className="text-sm text-slate-300 leading-relaxed">{dec.rationale}</p>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           )}

//           {/* ── ACHIEVEMENTS ── */}
//           {activeTab === 'achievements' && (
//             <div className="p-8">
//               <div className="grid sm:grid-cols-2 gap-4">
//                 {design.achievements.map((a, i) => (
//                   <div key={i} className="flex items-start gap-4 p-5 rounded-xl bg-[#13131e] border border-slate-800 hover:border-emerald-500/20 transition-colors">
//                     <div className="w-9 h-9 shrink-0 rounded-xl flex items-center justify-center text-lg"
//                       style={{ background: `${design.accentColor}18`, border: `1px solid ${design.accentColor}25` }}>
//                       🏆
//                     </div>
//                     <p className="text-sm text-slate-300 leading-relaxed pt-1">{a}</p>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           )}

//         </div>
//       </div>
//     </div>
//   );
// };

// // ════════════════════════════════════════════════════════════════
// //  MAIN EXPORT
// // ════════════════════════════════════════════════════════════════

// export const ProjectsSection: React.FC = () => {
//   const [activeFilter, setActiveFilter] = useState<Category>('All');
//   const [gallery, setGallery] = useState<{ project: Project; index: number } | null>(null);

//   const filtered = useMemo(
//     () => activeFilter === 'All' ? PROJECTS : PROJECTS.filter((p) => p.category.includes(activeFilter)),
//     [activeFilter]
//   );

//   const featured = filtered.filter((p) => p.featured);
//   const rest     = filtered.filter((p) => !p.featured);

//   const openGallery = useCallback((project: Project, index: number) => {
//     setGallery({ project, index });
//   }, []);

//   const closeGallery = useCallback(() => setGallery(null), []);

//   return (
//     <>
//       {/* Gallery modal */}
//       {gallery && (
//         <GalleryModal
//           images={gallery.project.images}
//           initialIndex={gallery.index}
//           projectTitle={gallery.project.title}
//           onClose={closeGallery}
//         />
//       )}

//       <section id="portfolio" className="py-28 bg-[#0a0a0f]">
//         <div className="max-w-6xl mx-auto px-6">

//           {/* ── Section Header ── */}
//           <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
//             <div>
//               <p className="text-emerald-400 font-mono text-sm tracking-widest uppercase mb-3">Work</p>
//               <h2 className="text-4xl md:text-5xl font-black text-white">Projects</h2>
//               <p className="text-slate-400 mt-3 max-w-xl">
//                 Production systems built across trading, e-commerce, HRMS, VOIP, and real-time domains — 9.5+ years of shipped code.
//               </p>
//             </div>
//             <div className="flex gap-6 shrink-0">
//               {[
//                 { value: `${PROJECTS.length}`, label: 'Projects' },
//                 { value: `${PROJECTS.filter((p) => p.status === 'Live').length}`, label: 'Live' },
//                 { value: '10K+', label: 'Users Served' },
//               ].map((s) => (
//                 <div key={s.label} className="text-center">
//                   <div className="text-2xl font-black text-emerald-400">{s.value}</div>
//                   <div className="text-xs text-slate-500 font-mono uppercase tracking-wider">{s.label}</div>
//                 </div>
//               ))}
//             </div>
//           </div>

//           {/* ── Filter tabs ── */}
//           <div className="flex flex-wrap gap-2 mb-12">
//             {CATEGORIES.map((cat) => {
//               const count = cat === 'All' ? PROJECTS.length : PROJECTS.filter((p) => p.category.includes(cat)).length;
//               return (
//                 <button key={cat} onClick={() => setActiveFilter(cat)}
//                   className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-semibold transition-all duration-200 ${
//                     activeFilter === cat
//                       ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300'
//                       : 'bg-[#13131e] border-slate-800 text-slate-400 hover:border-slate-600 hover:text-slate-200'
//                   }`}>
//                   {cat}
//                   <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${activeFilter === cat ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-600'}`}>
//                     {count}
//                   </span>
//                 </button>
//               );
//             })}
//           </div>

//           {/* ── Empty ── */}
//           {filtered.length === 0 && (
//             <div className="text-center py-20">
//               <div className="text-5xl mb-4">🔍</div>
//               <p className="font-mono text-slate-600">No projects in this category.</p>
//             </div>
//           )}

//           {/* ── Featured (3-col on large) ── */}
//           {featured.length > 0 && (
//             <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
//               {featured.map((p) => <FeaturedCard key={p.id} project={p} onOpenGallery={openGallery} />)}
//             </div>
//           )}

//           {/* ── Rest ── */}
//           {rest.length > 0 && (
//             <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
//               {rest.map((p) => <ProjectCard key={p.id} project={p} onOpenGallery={openGallery} />)}
//             </div>
//           )}

//           {/* ── GitHub CTA ── */}
//           <div className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 rounded-2xl bg-[#13131e] border border-slate-800">
//             <div>
//               <div className="font-bold text-white">Want to see more?</div>
//               <div className="text-sm text-slate-400">Full source code and open-source work on GitHub and GitLab.</div>
//             </div>
//             <div className="flex gap-3 shrink-0">
//               <a href="https://github.com/piyushbhai" target="_blank" rel="noreferrer"
//                 className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-slate-700 hover:border-emerald-500/40 text-slate-300 hover:text-emerald-400 font-semibold text-sm transition-all duration-200">
//                 <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/></svg>
//                 GitHub
//               </a>
//               <a href="https://gitlab.com/piyushprajapati/myportfolio" target="_blank" rel="noreferrer"
//                 className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-slate-700 hover:border-orange-500/40 text-slate-300 hover:text-orange-400 font-semibold text-sm transition-all duration-200">
//                 <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M4.845.904C4.605.904 4.4 1.088 4.354 1.32L.014 17.88c-.06.25.04.51.26.645l11.741 7.456a.51.51 0 00.54 0l11.741-7.456c.22-.135.32-.395.26-.645L20.216 1.32C20.17 1.088 19.965.904 19.725.904h-4.46L13.5 10.48 12 5.97l-1.5 4.51L8.735.904H4.845z"/></svg>
//                 GitLab
//               </a>
//             </div>
//           </div>

//           {/* ── System Design ── */}
//           <SystemDesignSection />

//         </div>
//       </section>
//     </>
//   );
// };

// export default ProjectsSection;
